import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import useGenerateChallenge from '@/features/2fa/hooks/useGenerateChallenge';
import { changePassword, requestChangePassword } from '@/features/dashboard-users/store/support-user.actions';
import { useCurrentSupportUser } from '@/features/dashboard-users/hooks/useCurrentSupportUser';
import { useChangePasswordForm } from '@/features/dashboard-users/hooks/changePassword/useChangePasswordForm';

export function useChangeOwnPassword() {
  const dispatch = useDispatch();

  const { id, isCurrentSupportUserLoading } = useCurrentSupportUser();
  const { generateChallenge } = useGenerateChallenge();

  const {
    currentPasswordWatch,
    newPasswordWatch,
    repeatPassphraseWatch,
    confirmNewPasswordWatch,
    passphraseWatch,
    isNewAndConfirmPasswordTheSame,
    isNewPasswordDifferentFromCurrent,
    isCaracterNumberValid,
    isPasswordContainsLowercase,
    isPasswordContainsUppercase,
    isPasswordContainsSpecialChar,
    isPasswordContainsNumber,
    isNewPasswordValid,
    handleInputChange,
  } = useChangePasswordForm();

  const onSavePassword = useCallback(
    (onFinishGenerateChallenge?: (challenge: string) => void) => {
      dispatch(requestChangePassword());
      generateChallenge((challenge) => onFinishGenerateChallenge?.(challenge));
    },
    [dispatch, generateChallenge],
  );

  const isFormValid = useMemo(
    () =>
      currentPasswordWatch && isNewPasswordValid && isNewAndConfirmPasswordTheSame && isNewPasswordDifferentFromCurrent,
    [currentPasswordWatch, isNewPasswordValid, isNewAndConfirmPasswordTheSame, isNewPasswordDifferentFromCurrent],
  );

  const updatePassword = useCallback(
    (challenge: string, onFinish?: () => void) => {
      dispatch(
        changePassword(
          id,
          {
            currentPassword: currentPasswordWatch,
            newPassword: newPasswordWatch,
            credentials: challenge,
          },
          () => onFinish?.(),
        ),
      );
    },
    [currentPasswordWatch, dispatch, id, newPasswordWatch],
  );

  return {
    currentPasswordWatch,
    newPasswordWatch,
    confirmNewPasswordWatch,
    isNewAndConfirmPasswordTheSame,
    isCaracterNumberValid,
    isPasswordContainsLowercase,
    isPasswordContainsUppercase,
    isPasswordContainsSpecialChar,
    isFormValid,
    isPasswordContainsNumber,
    isCurrentSupportUserLoading,
    isNewPasswordDifferentFromCurrent,
    isNewPasswordValid,
    passphraseWatch,
    repeatPassphraseWatch,
    handleInputChange,
    onSavePassword,
    updatePassword,
  };
}
